/**
 * Normaliza cualquier imagen elegida desde el dashboard (compu o celular) a
 * JPEG antes de subirla — así no importa si Angie adjunta un PNG, un WEBP o
 * un JPG, todo termina en el mismo formato que ya espera el resto del
 * sistema (ver `subirImagen` en sincronizador.ts, siempre 'image/jpeg').
 *
 * HEIC se rechaza con un mensaje claro en vez de convertirse — 2026-09-16:
 * se probó `heic2any` (decodificador HEIC en el navegador) y distorsionó los
 * colores de un HEIC generado por un Samsung Galaxy A32 (patrón rojo/cian,
 * típico de un decode HEIF mal interpretado para códecs no-Apple) — un fallo
 * SILENCIOSO (la foto se sube igual, solo con colores mal) es peor que un
 * rechazo visible, así que se revirtió. Ver el mismo criterio que ya usa la
 * captura por cámara en mobile.
 */

export const TAMANO_MAXIMO_BYTES = 15 * 1024 * 1024 // 15MB — generoso para una foto de celular/tablet, corta archivos disparatados.

export class ErrorImagenNoSoportada extends Error {}

function pareceHeic(archivo: File): boolean {
  return /image\/hei[cf]/i.test(archivo.type) || /\.hei[cf]$/i.test(archivo.name)
}

export async function normalizarImagenAJpeg(archivo: File): Promise<Blob> {
  if (archivo.size === 0) {
    throw new ErrorImagenNoSoportada('El archivo está vacío o corrupto, elige otra foto.')
  }
  if (archivo.size > TAMANO_MAXIMO_BYTES) {
    throw new ErrorImagenNoSoportada('La foto pesa demasiado (máximo 15MB), elige una más liviana.')
  }
  if (pareceHeic(archivo)) {
    throw new ErrorImagenNoSoportada('Formato HEIC no soportado — exporta la foto como JPG o PNG antes de subirla.')
  }
  if (archivo.type === 'image/jpeg') return archivo // ya está en el formato final, no hace falta recodificar.

  let bitmap: ImageBitmap
  try {
    bitmap = await createImageBitmap(archivo)
  } catch {
    throw new ErrorImagenNoSoportada('No se pudo leer esta imagen, prueba con otro archivo.')
  }

  try {
    const canvas = document.createElement('canvas')
    canvas.width = bitmap.width
    canvas.height = bitmap.height
    const contexto = canvas.getContext('2d')
    if (!contexto) throw new Error('No se pudo preparar el lienzo para convertir la imagen.')
    contexto.drawImage(bitmap, 0, 0)

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9))
    if (!blob) throw new ErrorImagenNoSoportada('No se pudo convertir esta imagen a JPEG.')
    return blob
  } finally {
    bitmap.close()
  }
}
